import {
  Box,
  FormControl,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { HelpOutline } from "@mui/icons-material";
import { useEffect, useState } from "react";
import { VariableField } from "../../components/PopOutTextDialog";

interface ThanksioIntegration {
  id: string;
  instanceName: string;
}

interface SequenceThanksioFormProps {
  actionParameters: any;
  setActionParameters: (params: any) => void;
  content: string;
  setContent: (content: string) => void;
  variableFields: VariableField[];
  customVariableFields?: VariableField[];
  thanksioIntegrations?: ThanksioIntegration[];
}

// Thanks.io mail types
const MAIL_TYPES = [
  { value: "postcard", label: "Postcard", usesImage: true },
  { value: "letter", label: "Letter", usesImage: true },
  { value: "notecard", label: "Notecard", usesImage: true },
  { value: "windowlessletter", label: "Windowless Letter (PDF)", usesImage: false },
  { value: "selfmailer", label: "Self Mailer (PDF)", usesImage: false },
];

// Handwriting styles available in Thanks.io
const HANDWRITING_STYLES = [
  { value: 1, label: "Style 1 - Classic" },
  { value: 2, label: "Style 2 - Elegant" },
  { value: 3, label: "Style 3 - Casual" },
  { value: 4, label: "Style 4 - Modern" },
  { value: 5, label: "Style 5 - Bold" },
  { value: 6, label: "Style 6 - Script" },
  { value: 7, label: "Style 7 - Friendly" },
  { value: 8, label: "Style 8 - Professional" },
  { value: 9, label: "Style 9 - Artistic" },
  { value: 10, label: "Style 10 - Minimal" },
];

export default function SequenceThanksioForm({
  actionParameters,
  setActionParameters,
  content,
  setContent,
  variableFields,
  customVariableFields = [],
  thanksioIntegrations = [],
}: SequenceThanksioFormProps) {
  // Combine standard and custom variable fields
  const allVariableFields = [...variableFields, ...customVariableFields];

  // State for variable dropdown
  const [variableMenuAnchor, setVariableMenuAnchor] = useState<null | {
    element: HTMLElement;
    fieldName: string;
  }>(null);

  useEffect(() => {
    // Initialize all required fields with defaults
    const updates: any = {};
    let hasUpdates = false;

    if (actionParameters.mailType === undefined) {
      updates.mailType = "postcard";
      hasUpdates = true;
    }
    if (actionParameters.frontImageUrl === undefined) {
      updates.frontImageUrl = "";
      hasUpdates = true;
    }
    if (actionParameters.pdfUrl === undefined) {
      updates.pdfUrl = "";
      hasUpdates = true;
    }
    if (actionParameters.handwritingStyle === undefined) {
      updates.handwritingStyle = 4;
      hasUpdates = true;
    }

    if (hasUpdates) {
      setActionParameters({
        ...actionParameters,
        ...updates,
      });
    }
  }, [actionParameters, setActionParameters]);

  const selectedMailType = MAIL_TYPES.find(
    (mt) => mt.value === actionParameters.mailType
  );
  const usesImage = selectedMailType?.usesImage ?? true;

  const handleFieldChange = (
    fieldName: string,
    value: string,
    event?: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (fieldName === "content") {
      setContent(value);
    } else {
      setActionParameters({ ...actionParameters, [fieldName]: value });
    }

    // Check for "/" trigger
    if (event && event.target.selectionStart !== null) {
      const cursorPos = event.target.selectionStart;
      if (value[cursorPos - 1] === "/") {
        setVariableMenuAnchor({ element: event.target, fieldName });
      } else {
        setVariableMenuAnchor(null);
      }
    }
  };

  const insertVariable = (variable: string) => {
    if (!variableMenuAnchor) return;

    const { fieldName } = variableMenuAnchor;
    const currentValue = fieldName === "content" ? content : (actionParameters[fieldName] || "");
    // Remove the "/" and add the variable
    const newValue = currentValue.slice(0, -1) + `{{${variable}}}`;
    if (fieldName === "content") {
      setContent(newValue);
    } else {
      setActionParameters({ ...actionParameters, [fieldName]: newValue });
    }
    setVariableMenuAnchor(null);
  };

  const renderVariableMenu = () => (
    <Menu
      anchorEl={variableMenuAnchor?.element}
      open={Boolean(variableMenuAnchor)}
      onClose={() => setVariableMenuAnchor(null)}
      sx={{ maxHeight: 300 }}
    >
      {allVariableFields.map((field, i) => (
        <MenuItem key={i} onClick={() => insertVariable(field.key)}>
          {field.icon && <Box sx={{ mr: 1, display: "flex" }}>{field.icon}</Box>}
          {field.label}
        </MenuItem>
      ))}
    </Menu>
  );

  return (
    <Box>
      {/* Integration Selector */}
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="subtitle1"
          sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
        >
          Thanks.io Account
          <Tooltip title="Select which Thanks.io integration to use for sending mail.">
            <HelpOutline
              sx={{ fontSize: 16, color: "text.secondary", cursor: "help" }}
            />
          </Tooltip>
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Choose the Thanks.io account to send mail from.
        </Typography>
        {thanksioIntegrations.length === 0 ? (
          <Typography variant="body2" color="error">
            No Thanks.io integrations found. Please install a Thanks.io integration first.
          </Typography>
        ) : (
          <FormControl fullWidth>
            <InputLabel>Thanks.io Account</InputLabel>
            <Select
              label="Thanks.io Account"
              value={actionParameters.thanksioIntegrationId || ""}
              onChange={(e) =>
                setActionParameters({
                  ...actionParameters,
                  thanksioIntegrationId: e.target.value,
                })
              }
            >
              {thanksioIntegrations.map((integration) => (
                <MenuItem key={integration.id} value={integration.id}>
                  {integration.instanceName || "Thanks.io"}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography
          variant="subtitle1"
          sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
        >
          Mail Type
          <Tooltip title="Select the type of direct mail to send. Postcards, letters, and notecards use an image and handwritten message. Windowless letters and self mailers use a PDF.">
            <HelpOutline
              sx={{ fontSize: 16, color: "text.secondary", cursor: "help" }}
            />
          </Tooltip>
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Choose the format for your direct mail piece.
        </Typography>
        <FormControl fullWidth>
          <InputLabel>Mail Type</InputLabel>
          <Select
            label="Mail Type"
            value={actionParameters.mailType || "postcard"}
            onChange={(e) =>
              setActionParameters({
                ...actionParameters,
                mailType: e.target.value,
              })
            }
          >
            {MAIL_TYPES.map((mt) => (
              <MenuItem key={mt.value} value={mt.value}>
                {mt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {usesImage ? (
        <>
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="subtitle1"
              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
            >
              Front Image URL
              <Tooltip title="URL to the image that will appear on the front of the mail piece. This should be a publicly accessible URL to a high-quality image.">
                <HelpOutline
                  sx={{ fontSize: 16, color: "text.secondary", cursor: "help" }}
                />
              </Tooltip>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Provide a URL to your front image. Use {"{{variableName}}"} for
              dynamic content.
            </Typography>
            <TextField
              fullWidth
              placeholder="https://example.com/your-image.png"
              variant="outlined"
              value={actionParameters.frontImageUrl || ""}
              onChange={(e) =>
                handleFieldChange("frontImageUrl", e.target.value, e)
              }
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography
              variant="subtitle1"
              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
            >
              Message
              <Tooltip title="The handwritten message that will appear on the mail piece. Use {{firstName}}, {{lastName}}, etc. for personalization.">
                <HelpOutline
                  sx={{ fontSize: 16, color: "text.secondary", cursor: "help" }}
                />
              </Tooltip>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Write your handwritten message. Use {"{{variableName}}"} for
              personalization.
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder="Hey {{firstName}}!

Thank you for your interest in our services. We'd love to help you achieve your goals.

Best regards,
Your Team"
              variant="outlined"
              value={content || ""}
              onChange={(e) => handleFieldChange("content", e.target.value, e)}
              helperText={
                <>
                  Type{" "}
                  <Box
                    component="span"
                    sx={{
                      bgcolor: "action.hover",
                      px: 0.5,
                      borderRadius: 0.5,
                      fontFamily: "monospace",
                    }}
                  >
                    /
                  </Box>{" "}
                  to insert member variables
                </>
              }
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography
              variant="subtitle1"
              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
            >
              Handwriting Style
              <Tooltip title="Choose the handwriting style for your message. Different styles give different looks to your handwritten text.">
                <HelpOutline
                  sx={{ fontSize: 16, color: "text.secondary", cursor: "help" }}
                />
              </Tooltip>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Select a handwriting style for your message.
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Handwriting Style</InputLabel>
              <Select
                label="Handwriting Style"
                value={actionParameters.handwritingStyle || 4}
                onChange={(e) =>
                  setActionParameters({
                    ...actionParameters,
                    handwritingStyle: e.target.value,
                  })
                }
              >
                {HANDWRITING_STYLES.map((style) => (
                  <MenuItem key={style.value} value={style.value}>
                    {style.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </>
      ) : (
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="subtitle1"
            sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
          >
            PDF URL
            <Tooltip title="URL to the PDF document that will be printed and mailed. This should be a publicly accessible URL to your PDF file.">
              <HelpOutline
                sx={{ fontSize: 16, color: "text.secondary", cursor: "help" }}
              />
            </Tooltip>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Provide a URL to your PDF document. Use {"{{variableName}}"} for
            dynamic URLs.
          </Typography>
          <TextField
            fullWidth
            placeholder="https://example.com/your-document.pdf"
            variant="outlined"
            value={actionParameters.pdfUrl || ""}
            onChange={(e) => handleFieldChange("pdfUrl", e.target.value, e)}
          />
        </Box>
      )}

      <Box
        sx={{
          p: 2,
          bgcolor: "action.hover",
          borderRadius: 1,
          mb: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          <strong>Required recipient fields:</strong> Members must have address
          fields in their data for Thanks.io to send mail. Required fields:
          address, city, state, and zip. Country is optional and defaults to US.
        </Typography>
      </Box>

      {renderVariableMenu()}
    </Box>
  );
}
